import React, { useMemo, useEffect } from 'react';
import { Student, ScheduleEntry, AgendaEntry, Teacher } from '../types';
import { BookOpen, ChevronRight } from 'lucide-react';

interface Props {
  student: Student;
  schedules: ScheduleEntry[];
  agendas: AgendaEntry[];
  teachers: Teacher[];
  onComplete: (subject: string) => void;
}

const SubjectSelectionView: React.FC<Props> = ({ student, schedules, agendas, teachers, onComplete }) => {
  const availableSubjects = useMemo(() => {
    if (!student || !student.class) return [];
    const subjectsFromSchedules = (schedules || []).filter(s => s.class === student.class).map(s => s.subject);
    const subjectsFromAgendas = (agendas || []).filter(a => a.class === student.class).map(a => a.subject);
    const subjectsFromTeachers = (teachers || []).filter(t => (t.assignedClasses || []).includes(student.class)).map(t => t.subject);
    return Array.from(new Set([...subjectsFromSchedules, ...subjectsFromAgendas, ...subjectsFromTeachers].filter(Boolean)));
  }, [schedules, agendas, teachers, student]);

  useEffect(() => {
    if (availableSubjects.length === 1) {
      onComplete(availableSubjects[0]);
    }
  }, [availableSubjects, onComplete]);

  return (
    <div className="flex items-center justify-center py-6 sm:py-12">
      <div className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800 w-full max-w-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full -mr-16 -mt-16"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-2xl">
              <BookOpen className="text-indigo-600 dark:text-indigo-400" size={28} />
            </div>
            <h2 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">Pilih Mapel</h2>
          </div>
          <p className="text-sm font-bold text-slate-500 mb-8">Silakan pilih mata pelajaran untuk melihat detail akademik dan kehadiran Anda.</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {availableSubjects.length > 0 ? (
              availableSubjects.map(subject => (
                <button
                  key={subject}
                  onClick={() => onComplete(subject)}
                  className="p-5 rounded-3xl border border-slate-200 dark:border-slate-800 hover:border-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-700 dark:hover:text-indigo-300 transition-all flex items-center justify-between group text-left shadow-sm hover:shadow-md"
                >
                  <span className="font-bold text-slate-700 dark:text-slate-300 group-hover:text-indigo-700 dark:group-hover:text-indigo-300">{subject}</span>
                  <div className="w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center group-hover:bg-indigo-100 dark:group-hover:bg-indigo-800/50 transition-colors">
                    <ChevronRight size={18} className="text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400" />
                  </div>
                </button>
              ))
            ) : (
              <div className="col-span-full p-6 text-center text-slate-500 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-200 dark:border-slate-800">
                Belum ada mata pelajaran yang tersedia untuk kelas Anda.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectSelectionView;
